import { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';

// Offline-first data layer
import { saveDataEntry, FORM_TYPES } from 'utils/offlineFirstDataLayer';

// material-ui
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useTheme, alpha } from '@mui/material/styles';

// ant design icons
import PlusOutlined from '@ant-design/icons/PlusOutlined';
import DeleteOutlined from '@ant-design/icons/DeleteOutlined';
import CheckOutlined from '@ant-design/icons/CheckOutlined';
import EditOutlined from '@ant-design/icons/EditOutlined';
import CalendarOutlined from '@ant-design/icons/CalendarOutlined';
import BookOutlined from '@ant-design/icons/BookOutlined';
import FileSearchOutlined from '@ant-design/icons/FileSearchOutlined';
import UploadOutlined from '@ant-design/icons/UploadOutlined';
import FileOutlined from '@ant-design/icons/FileOutlined';
import CloseCircleOutlined from '@ant-design/icons/CloseCircleOutlined';

// project imports
import MainCard from 'components/MainCard';

// Teaching method keys (translated at render time)
const TEACHING_METHOD_KEYS = ['demonstration', 'explanation', 'experiment', 'discussion'];


// Initial objectives state for new entries
const createInitialObjectives = () => ({
  standard: {
    approaching: [''],
    meeting: [''],
    exceeding: ['']
  },
  modified: {
    approaching: [''],
    meeting: [''],
    exceeding: ['']
  }
});

const LessonPlanMode = ({
  viewMode = 'individual',
  onDataChange,
  childName = '',
  childId,
  therapistId,
  therapistName = '',
  sessionId = null,
  orgId = null,
  initialPlans = [],
}) => {
  const theme = useTheme();
  const intl = useIntl();

  // Saving state to prevent duplicate saves
  const [saving, setSaving] = useState(false);

  // Ref to skip auto-save on initial render
  const initialRenderRef = useRef(true);

  // Level configuration using theme palette colors
  const LEVELS = useMemo(() => [
    {
      key: 'approaching',
      label: intl.formatMessage({ id: 'lessonPlan.level.approaching' }),
      color: theme.palette.text.secondary,
      dotColor: theme.palette.warning.main,
      bgLight: alpha(theme.palette.grey[500], 0.06),
      bgHover: alpha(theme.palette.grey[500], 0.12)
    },
    {
      key: 'meeting',
      label: intl.formatMessage({ id: 'lessonPlan.level.meeting' }),
      color: theme.palette.text.secondary,
      dotColor: theme.palette.success.main,
      bgLight: alpha(theme.palette.grey[500], 0.06),
      bgHover: alpha(theme.palette.grey[500], 0.12)
    },
    {
      key: 'exceeding',
      label: intl.formatMessage({ id: 'lessonPlan.level.exceeding' }),
      color: theme.palette.text.secondary,
      dotColor: theme.palette.info.main,
      bgLight: alpha(theme.palette.grey[500], 0.06),
      bgHover: alpha(theme.palette.grey[500], 0.12)
    }
  ], [theme, intl]);

  // Lesson plan records
  const [lessonPlans, setLessonPlans] = useState(initialPlans);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);

  // Form state for add/edit
  const [formData, setFormData] = useState({
    dateOfIntroduction: new Date().toISOString().split('T')[0],
    lessonTopic: '',
    grade: '',
    subject: '',
    volume: '',
    blockNo: '',
    objectives: createInitialObjectives(),
    selectedMethods: [],
    otherMethod: '',
    resources: '',
    resourceFiles: []
  });

  // Auto-save: notify parent when form data changes so it can debounce and persist
  useEffect(() => {
    if (initialRenderRef.current) {
      initialRenderRef.current = false;
      return;
    }

    // Skip if no meaningful data has been entered
    const hasLessonTopic = formData.lessonTopic.trim().length > 0;
    const hasLessonPlans = lessonPlans.length > 0;
    if (!hasLessonTopic && !hasLessonPlans) return;

    const autoSaveData = {
      lessonPlans,
      currentForm: {
        ...formData,
        editingRecordId: editingRecord?.id || null
      }
    };

    if (onDataChange) onDataChange('lesson-plan', autoSaveData);
  }, [formData, lessonPlans, editingRecord]); // eslint-disable-line react-hooks/exhaustive-deps

  // Notify parent of data change
  const notifyChange = useCallback(() => {
    if (onDataChange) onDataChange();
  }, [onDataChange]);

  // Reset form
  const resetForm = () => {
    setFormData({
      dateOfIntroduction: new Date().toISOString().split('T')[0],
      lessonTopic: '',
      grade: '',
      subject: '',
      volume: '',
      blockNo: '',
      objectives: createInitialObjectives(),
      selectedMethods: [],
      otherMethod: '',
      resources: '',
      resourceFiles: []
    });
    setEditingRecord(null);
    setShowAddForm(false);
  };

  // Handle form field changes
  const handleFormField = (field) => (e) => {
    setFormData((prev) => ({ ...prev, [field]: e.target.value }));
    notifyChange();
  };

  // Handle objective point change
  const handlePointChange = (panel, level, index, value) => {
    setFormData((prev) => {
      const updated = { ...prev };
      updated.objectives = { ...updated.objectives };
      updated.objectives[panel] = { ...updated.objectives[panel] };
      updated.objectives[panel][level] = [...updated.objectives[panel][level]];
      updated.objectives[panel][level][index] = value;
      return updated;
    });
    notifyChange();
  };

  // Add point to both standard and modified panels (synced)
  const handleAddPoint = (level) => {
    setFormData((prev) => {
      const updated = { ...prev };
      updated.objectives = { ...updated.objectives };
      updated.objectives.standard = { ...updated.objectives.standard };
      updated.objectives.modified = { ...updated.objectives.modified };
      updated.objectives.standard[level] = [...updated.objectives.standard[level], ''];
      updated.objectives.modified[level] = [...updated.objectives.modified[level], ''];
      return updated;
    });
    notifyChange();
  };

  // Remove point from both panels (synced)
  const handleRemovePoint = (level, index) => {
    setFormData((prev) => {
      const updated = { ...prev };
      updated.objectives = { ...updated.objectives };
      updated.objectives.standard = { ...updated.objectives.standard };
      updated.objectives.modified = { ...updated.objectives.modified };

      if (updated.objectives.standard[level].length > 1) {
        updated.objectives.standard[level] = updated.objectives.standard[level].filter((_, i) => i !== index);
        updated.objectives.modified[level] = updated.objectives.modified[level].filter((_, i) => i !== index);
      } else {
        updated.objectives.standard[level] = [''];
        updated.objectives.modified[level] = [''];
      }
      return updated;
    });
    notifyChange();
  };

  // Toggle teaching method
  const handleToggleMethod = (method) => {
    setFormData((prev) => ({
      ...prev,
      selectedMethods: prev.selectedMethods.includes(method)
        ? prev.selectedMethods.filter((m) => m !== method)
        : [...prev.selectedMethods, method]
    }));
    notifyChange();
  };

  // Handle file upload for resources
  const handleFileUpload = (event) => {
    const files = Array.from(event.target.files);
    const newFiles = files.map((file) => ({
      id: Date.now() + Math.random(),
      name: file.name,
      size: file.size,
      file: file
    }));
    setFormData((prev) => ({
      ...prev,
      resourceFiles: [...prev.resourceFiles, ...newFiles]
    }));
    notifyChange();
    // Reset input so same file can be selected again
    event.target.value = '';
  };

  // Remove uploaded file
  const handleRemoveFile = (fileId) => {
    setFormData((prev) => ({
      ...prev,
      resourceFiles: prev.resourceFiles.filter((f) => f.id !== fileId)
    }));
    notifyChange();
  };

  // Format file size
  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  // Save lesson plan (add or update)
  const handleSave = async () => {
    if (saving) {
      return;
    }

    if (!formData.lessonTopic) return;

    setSaving(true);

    try {
      const newPlan = { id: editingRecord?.id || Date.now(), ...formData };

      // Save to IndexedDB for offline-first support
      await saveDataEntry(
        FORM_TYPES.LESSON_PLAN,
        newPlan,
        {
          childId,
          childName,
          therapistId,
          therapistName,
          sessionId,
          orgId,
          sessionDate: new Date().toISOString()
        }
      );

      if (editingRecord) {
        setLessonPlans((prev) =>
          prev.map((lp) => (lp.id === editingRecord.id ? newPlan : lp))
        );
      } else {
        setLessonPlans((prev) => [newPlan, ...prev]);
      }
      resetForm();
      notifyChange();
    } catch (error) {
      console.error('[LessonPlanMode] Failed to save lesson plan:', error);
    } finally {
      setSaving(false);
    }
  };

  // Edit record
  const handleEdit = (record) => {
    setEditingRecord(record);
    setFormData({
      dateOfIntroduction: record.dateOfIntroduction,
      lessonTopic: record.lessonTopic,
      grade: record.grade,
      subject: record.subject,
      volume: record.volume,
      blockNo: record.blockNo,
      objectives: record.objectives,
      selectedMethods: record.selectedMethods || [],
      otherMethod: record.otherMethod || '',
      resources: record.resources,
      resourceFiles: record.resourceFiles || []
    });
    setShowAddForm(true);
  };

  // Delete record
  const handleDelete = (id) => {
    setLessonPlans((prev) => prev.filter((lp) => lp.id !== id));
    notifyChange();
  };

  // Grid view inline edit
  const handleGridFieldChange = (id, field, value) => {
    setLessonPlans((prev) =>
      prev.map((lp) => (lp.id === id ? { ...lp, [field]: value } : lp))
    );
    notifyChange();
  };

  // Add empty row in grid view
  const handleAddGridRow = () => {
    const newPlan = {
      id: Date.now(),
      dateOfIntroduction: new Date().toISOString().split('T')[0],
      lessonTopic: '',
      grade: '',
      subject: '',
      volume: '',
      blockNo: '',
      objectives: createInitialObjectives(),
      selectedMethods: [],
      otherMethod: '',
      resources: '',
      resourceFiles: []
    };
    setLessonPlans((prev) => [newPlan, ...prev]);
    notifyChange();
  };

  // Count total objective points for a record
  const countObjectives = (record) => {
    let count = 0;
    ['approaching', 'meeting', 'exceeding'].forEach((level) => {
      count += (record.objectives?.standard?.[level] || []).filter((p) => p.trim()).length;
      count += (record.objectives?.modified?.[level] || []).filter((p) => p.trim()).length;
    });
    return count;
  };

  // Section title with accent bar
  const SectionTitle = ({ children }) => (
    <Typography variant="h6" sx={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1.5 }}>
      <Box sx={{ width: 4, height: 20, borderRadius: 1, bgcolor: 'primary.main' }} />
      {children}
    </Typography>
  );

  // Render objective points for a level within a panel
  const renderPoints = (panel, level, levelConfig) => {
    const points = formData.objectives[panel][level];
    const isModified = panel === 'modified';

    return (
      <Stack spacing={1} sx={{ px: 2, pb: 2 }}>
        {points.map((point, index) => (
          <Stack key={index} direction="row" spacing={1} alignItems="flex-start">
            <Box
              sx={{
                minWidth: 28,
                height: 28,
                borderRadius: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '0.75rem',
                fontWeight: 600,
                mt: 1,
                bgcolor: levelConfig.bgLight,
                color: levelConfig.color
              }}
            >
              {index + 1}
            </Box>
            <TextField
              fullWidth
              size="small"
              multiline
              minRows={3}
              maxRows={3}
              placeholder={isModified ? intl.formatMessage({ id: 'lessonPlan.placeholder.modifiedObjective' }) : intl.formatMessage({ id: 'lessonPlan.placeholder.objectivePoint' })}
              value={point}
              onChange={(e) => handlePointChange(panel, level, index, e.target.value)}
              sx={{
                '& .MuiOutlinedInput-root': {
                  '&.Mui-focused fieldset': {
                    borderColor: theme.palette.primary.main
                  }
                }
              }}
            />
            <IconButton
              size="small"
              color="error"
              onClick={() => handleRemovePoint(level, index)}
              sx={{ mt: 0.5 }}
            >
              <DeleteOutlined />
            </IconButton>
          </Stack>
        ))}
      </Stack>
    );
  };

  // Render a level section (Approaching / Meeting / Exceeding)
  const renderLevelSection = (panel, levelConfig) => (
    <Box key={levelConfig.key} sx={{ borderBottom: `1px solid ${theme.palette.divider}`, '&:last-child': { borderBottom: 'none' } }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ px: 2, py: 1.5 }}>
        <Stack direction="row" spacing={1} alignItems="center">
          <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: levelConfig.dotColor }} />
          <Typography
            variant="caption"
            sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5, color: levelConfig.color }}
          >
            {levelConfig.label}
          </Typography>
        </Stack>
        <Button
          size="small"
          startIcon={<PlusOutlined />}
          onClick={() => handleAddPoint(levelConfig.key)}
          sx={{
            fontSize: '0.75rem',
            fontWeight: 600,
            color: 'primary.main'
          }}
        >
          {intl.formatMessage({ id: 'lessonPlan.addTarget' })}
        </Button>
      </Stack>
      {renderPoints(panel, levelConfig.key, levelConfig)}
    </Box>
  );

  // Render an objectives panel (Standard or Modified)
  const renderObjectivesPanel = (panel, title, icon, panelColor) => (
    <Paper
      elevation={0}
      sx={{ borderRadius: 2, overflow: 'hidden', border: `1px solid ${alpha(panelColor, 0.25)}`, flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}
    >
      <Box sx={{ bgcolor: alpha(panelColor, 0.08), py: 1.5, px: 2, textAlign: 'center', borderBottom: `1px solid ${alpha(panelColor, 0.15)}` }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 600, color: panelColor }}>
          {icon} {title}
        </Typography>
      </Box>
      <Box sx={{ flex: 1 }}>
        {LEVELS.map((level) => renderLevelSection(panel, level))}
      </Box>
    </Paper>
  );

  // Render a single read-only objectives panel
  const renderReadOnlyPanel = (record, panelKey, title, panelColor) => (
    <Paper
      elevation={0}
      sx={{
        borderRadius: 2,
        overflow: 'hidden',
        border: `1px solid ${alpha(panelColor, 0.25)}`,
        flex: 1,
        minWidth: 0,
        display: 'flex',
        flexDirection: 'column'
      }}
    >
      <Box sx={{ bgcolor: alpha(panelColor, 0.08), py: 1, px: 2, textAlign: 'center', borderBottom: `1px solid ${alpha(panelColor, 0.15)}` }}>
        <Typography variant="caption" fontWeight={600} sx={{ color: panelColor }}>{title}</Typography>
      </Box>
      {LEVELS.map((lvl) => {
        const points = (record.objectives?.[panelKey]?.[lvl.key] || []).filter((p) => p.trim());
        if (points.length === 0) return null;
        return (
          <Box key={lvl.key} sx={{ px: 2, py: 1, borderBottom: `1px solid ${theme.palette.divider}`, '&:last-child': { borderBottom: 'none' } }}>
            <Typography variant="caption" sx={{ fontWeight: 600, color: lvl.color, textTransform: 'uppercase' }}>
              {lvl.label}
            </Typography>
            {points.map((pt, i) => (
              <Typography key={i} variant="body2" sx={{ pl: 1, py: 0.25 }}>
                {i + 1}. {pt}
              </Typography>
            ))}
          </Box>
        );
      })}
    </Paper>
  );

  // Render read-only objectives for a record (individual view cards)
  const renderReadOnlyObjectives = (record) => (
    <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', md: 'row' }, alignItems: 'stretch' }}>
      {renderReadOnlyPanel(record, 'standard', intl.formatMessage({ id: 'lessonPlan.objectivesHeader' }), theme.palette.primary.main)}
      {renderReadOnlyPanel(record, 'modified', intl.formatMessage({ id: 'lessonPlan.modifiedObjectivesHeader' }), theme.palette.warning.dark)}
    </Box>
  );

  // ──────────────── ADD / EDIT FORM ────────────────
  const renderForm = () => (
    <Paper sx={{ p: 3, border: 1, borderColor: 'divider', bgcolor: 'grey.50', '& .MuiOutlinedInput-root': { bgcolor: 'background.paper' } }}>
      <Stack spacing={3}>
        {/* Lesson Info */}
        <SectionTitle>{intl.formatMessage({ id: 'lessonPlan.section.lessonInformation' })}</SectionTitle>
        <Grid container spacing={2.5}>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label={intl.formatMessage({ id: 'lessonPlan.label.dateOfIntroduction' })}
              type="date"
              value={formData.dateOfIntroduction}
              onChange={handleFormField('dateOfIntroduction')}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label={intl.formatMessage({ id: 'lessonPlan.label.lessonAndTopic' })}
              placeholder={intl.formatMessage({ id: 'lessonPlan.placeholder.lessonAndTopic' })}
              value={formData.lessonTopic}
              onChange={handleFormField('lessonTopic')}
            />
          </Grid>
        </Grid>

        {/* Class Details */}
        <SectionTitle>{intl.formatMessage({ id: 'lessonPlan.section.classDetails' })}</SectionTitle>
        <Grid container spacing={2.5}>
          <Grid item xs={6} sm={3}>
            <TextField fullWidth label={intl.formatMessage({ id: 'lessonPlan.label.grade' })} placeholder={intl.formatMessage({ id: 'lessonPlan.placeholder.grade' })} value={formData.grade} onChange={handleFormField('grade')} />
          </Grid>
          <Grid item xs={6} sm={3}>
            <TextField fullWidth label={intl.formatMessage({ id: 'lessonPlan.label.subject' })} placeholder={intl.formatMessage({ id: 'lessonPlan.placeholder.subject' })} value={formData.subject} onChange={handleFormField('subject')} />
          </Grid>
          <Grid item xs={6} sm={3}>
            <TextField fullWidth label={intl.formatMessage({ id: 'lessonPlan.label.volume' })} placeholder={intl.formatMessage({ id: 'lessonPlan.placeholder.volume' })} value={formData.volume} onChange={handleFormField('volume')} />
          </Grid>
          <Grid item xs={6} sm={3}>
            <TextField fullWidth label={intl.formatMessage({ id: 'lessonPlan.label.blockNo' })} placeholder={intl.formatMessage({ id: 'lessonPlan.placeholder.blockNo' })} value={formData.blockNo} onChange={handleFormField('blockNo')} />
          </Grid>
        </Grid>

        {/* Learning Objectives - side by side panels */}
        <SectionTitle>{intl.formatMessage({ id: 'lessonPlan.section.learningObjectives' })}</SectionTitle>
        <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', md: 'row' }, alignItems: 'stretch' }}>
          {renderObjectivesPanel('standard', intl.formatMessage({ id: 'lessonPlan.objectives' }), '\u2714', theme.palette.primary.main)}
          {renderObjectivesPanel('modified', intl.formatMessage({ id: 'lessonPlan.modifiedObjectives' }), '\u270E', theme.palette.warning.dark)}
        </Box>

        {/* Teaching Method */}
        <SectionTitle>{intl.formatMessage({ id: 'lessonPlan.section.teachingMethod' })}</SectionTitle>
        <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap>
          {TEACHING_METHOD_KEYS.map((method) => {
            const isSelected = formData.selectedMethods.includes(method);
            return (
              <Chip
                key={method}
                label={intl.formatMessage({ id: `lessonPlan.method.${method}` })}
                clickable
                onClick={() => handleToggleMethod(method)}
                icon={isSelected ? <CheckOutlined /> : undefined}
                sx={{
                  px: 1.5,
                  py: 2.5,
                  fontSize: '0.92rem',
                  fontWeight: 500,
                  borderRadius: 3,
                  border: '1.5px solid',
                  borderColor: isSelected ? 'primary.main' : theme.palette.grey[300],
                  bgcolor: isSelected ? 'primary.main' : 'background.paper',
                  color: isSelected ? 'white' : theme.palette.text.primary,
                  cursor: 'pointer',
                  '&:hover': {
                    bgcolor: isSelected ? 'primary.dark' : alpha(theme.palette.primary.main, 0.06),
                    borderColor: 'primary.main'
                  },
                  '& .MuiChip-icon': { color: 'white' }
                }}
              />
            );
          })}
          <TextField
            size="small"
            placeholder={intl.formatMessage({ id: 'lessonPlan.placeholder.otherMethod' })}
            value={formData.otherMethod}
            onChange={handleFormField('otherMethod')}
            sx={{ minWidth: 220 }}
          />
        </Stack>

        {/* Resources */}
        <SectionTitle>{intl.formatMessage({ id: 'lessonPlan.section.resources' })}</SectionTitle>
        <Stack spacing={2}>
          <TextField
            fullWidth
            multiline
            rows={4}
            placeholder={intl.formatMessage({ id: 'lessonPlan.placeholder.resources' })}
            value={formData.resources}
            onChange={handleFormField('resources')}
          />

          {/* File Upload Section */}
          <Box>
            <input
              type="file"
              id="resource-file-upload"
              multiple
              onChange={handleFileUpload}
              style={{ display: 'none' }}
              accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.jpg,.jpeg,.png,.gif"
            />
            <label htmlFor="resource-file-upload">
              <Button
                variant="outlined"
                component="span"
                startIcon={<UploadOutlined />}
                sx={{ mb: 1 }}
              >
                {intl.formatMessage({ id: 'lessonPlan.uploadDocuments' })}
              </Button>
            </label>
            <Typography variant="caption" color="text.secondary" sx={{ ml: 2 }}>
              {intl.formatMessage({ id: 'lessonPlan.supportedFormats' })}
            </Typography>
          </Box>

          {/* Uploaded Files List */}
          {formData.resourceFiles.length > 0 && (
            <Paper
              elevation={0}
              sx={{
                p: 2,
                border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                borderRadius: 2,
                bgcolor: alpha(theme.palette.primary.main, 0.02)
              }}
            >
              <Typography variant="caption" fontWeight={600} color="primary.main" sx={{ mb: 1.5, display: 'block' }}>
                {intl.formatMessage({ id: 'lessonPlan.uploadedFiles' }, { count: formData.resourceFiles.length })}
              </Typography>
              <Stack spacing={1}>
                {formData.resourceFiles.map((file) => (
                  <Box
                    key={file.id}
                    sx={{
                      p: 1.5,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      borderRadius: 1.5,
                      bgcolor: 'background.paper',
                      border: `1px solid ${theme.palette.divider}`,
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        borderColor: theme.palette.primary.main,
                        boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.15)}`
                      }
                    }}
                  >
                    <Stack direction="row" spacing={1.5} alignItems="center">
                      <Box
                        sx={{
                          width: 40,
                          height: 40,
                          borderRadius: 1,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          bgcolor: alpha(theme.palette.primary.main, 0.1)
                        }}
                      >
                        <FileOutlined style={{ fontSize: '1.25rem', color: theme.palette.primary.main }} />
                      </Box>
                      <Box>
                        <Typography variant="body2" fontWeight={600} sx={{ color: 'text.primary' }}>
                          {file.name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {formatFileSize(file.size)}
                        </Typography>
                      </Box>
                    </Stack>
                    <IconButton
                      size="small"
                      onClick={() => handleRemoveFile(file.id)}
                      sx={{
                        color: 'error.main',
                        '&:hover': {
                          bgcolor: alpha(theme.palette.error.main, 0.12)
                        }
                      }}
                    >
                      <CloseCircleOutlined style={{ fontSize: '1.25rem' }} />
                    </IconButton>
                  </Box>
                ))}
              </Stack>
            </Paper>
          )}
        </Stack>

        {/* Actions */}
        <Stack direction="row" spacing={2} justifyContent="flex-end">
          <Button variant="outlined" onClick={resetForm}>
            {intl.formatMessage({ id: 'common.cancel' })}
          </Button>
          {editingRecord && (
            <Button
              variant="outlined"
              color="error"
              startIcon={<DeleteOutlined />}
              onClick={() => {
                handleDelete(editingRecord.id);
                resetForm();
              }}
            >
              {intl.formatMessage({ id: 'common.delete' })}
            </Button>
          )}
          <Button
            variant="contained"
            startIcon={<CheckOutlined />}
            onClick={handleSave}
            disabled={!formData.lessonTopic || saving}
          >
            {saving ? intl.formatMessage({ id: 'common.saving' }) : (editingRecord ? intl.formatMessage({ id: 'lessonPlan.update' }) : intl.formatMessage({ id: 'common.save' }))}
          </Button>
        </Stack>
      </Stack>
    </Paper>
  );

  // ──────────────── INDIVIDUAL VIEW ────────────────
  const renderIndividualView = () => (
    <MainCard>
      <Stack spacing={2}>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Typography variant="h5">{intl.formatMessage({ id: 'lessonPlan.title' }, { count: lessonPlans.length })}</Typography>
          {!showAddForm && (
            <Button variant="contained" startIcon={<PlusOutlined />} onClick={() => setShowAddForm(true)}>
              {intl.formatMessage({ id: 'lessonPlan.new' })}
            </Button>
          )}
        </Stack>

        {showAddForm && renderForm()}

        {!showAddForm && lessonPlans.length === 0 ? (
          <Stack alignItems="center" spacing={2} sx={{ py: 6 }}>
            <FileSearchOutlined style={{ fontSize: '3rem', color: theme.palette.grey[300] }} />
            <Typography variant="h6" color="text.secondary">{intl.formatMessage({ id: 'lessonPlan.noPlansYet' })}</Typography>
            <Typography variant="body2" color="text.secondary">{intl.formatMessage({ id: 'lessonPlan.clickNewToCreate' })}</Typography>
          </Stack>
        ) : !showAddForm ? (
          <Stack spacing={2}>
            {lessonPlans.map((lp) => (
              <Paper
                key={lp.id}
                elevation={0}
                sx={{
                  p: 2.5,
                  border: 1,
                  borderColor: 'divider',
                  borderRadius: 2,
                  '&:hover': { boxShadow: theme.shadows[2] }
                }}
              >
                <Stack spacing={2}>
                  {/* Header */}
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
                      <CalendarOutlined style={{ fontSize: '1.2rem', color: theme.palette.text.secondary }} />
                      <Typography variant="subtitle2" fontWeight={600}>
                        {lp.dateOfIntroduction}
                      </Typography>
                      {lp.subject && (
                        <Chip label={lp.subject} size="small" color="primary" variant="outlined" />
                      )}
                      {lp.grade && (
                        <Chip label={lp.grade} size="small" variant="outlined" />
                      )}
                      {lp.blockNo && (
                        <Chip label={lp.blockNo} size="small" variant="outlined" />
                      )}
                    </Stack>
                    <Stack direction="row" spacing={1}>
                      <IconButton size="small" color="primary" onClick={() => handleEdit(lp)}>
                        <EditOutlined />
                      </IconButton>
                      <IconButton size="small" color="error" onClick={() => handleDelete(lp.id)}>
                        <DeleteOutlined />
                      </IconButton>
                    </Stack>
                  </Stack>

                  {/* Lesson Topic */}
                  <Paper
                    elevation={0}
                    sx={{
                      p: 2,
                      bgcolor: alpha(theme.palette.primary.main, 0.08),
                      borderRadius: 1.5,
                      border: `1px solid ${alpha(theme.palette.primary.main, 0.12)}`
                    }}
                  >
                    <Typography variant="caption" fontWeight={700} color="primary.main" sx={{ mb: 0.5, display: 'block' }}>
                      {intl.formatMessage({ id: 'lessonPlan.lessonAndTopicHeader' })}
                    </Typography>
                    <Typography variant="body2">{lp.lessonTopic || '-'}</Typography>
                  </Paper>

                  {/* Objectives */}
                  {renderReadOnlyObjectives(lp)}

                  {/* Methods & Resources row */}
                  <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', sm: 'row' } }}>
                    <Paper
                      elevation={0}
                      sx={{
                        p: 2,
                        flex: 1,
                        bgcolor: alpha(theme.palette.success.main, 0.08),
                        borderRadius: 1.5,
                        border: `1px solid ${alpha(theme.palette.success.main, 0.12)}`
                      }}
                    >
                      <Typography variant="caption" fontWeight={700} color="success.main" sx={{ mb: 0.5, display: 'block' }}>
                        {intl.formatMessage({ id: 'lessonPlan.teachingMethodsHeader' })}
                      </Typography>
                      <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                        {(lp.selectedMethods || []).map((m) => (
                          <Chip key={m} label={intl.formatMessage({ id: `lessonPlan.method.${m}`, defaultMessage: m })} size="small" color="success" variant="outlined" sx={{ mt: 0.5 }} />
                        ))}
                        {lp.otherMethod && (
                          <Chip label={lp.otherMethod} size="small" color="success" variant="outlined" sx={{ mt: 0.5 }} />
                        )}
                        {(!lp.selectedMethods || lp.selectedMethods.length === 0) && !lp.otherMethod && (
                          <Typography variant="body2">-</Typography>
                        )}
                      </Stack>
                    </Paper>

                    <Paper
                      elevation={0}
                      sx={{
                        p: 2,
                        flex: 1,
                        bgcolor: alpha(theme.palette.warning.main, 0.08),
                        borderRadius: 1.5,
                        border: `1px solid ${alpha(theme.palette.warning.main, 0.12)}`
                      }}
                    >
                      <Typography variant="caption" fontWeight={700} color="warning.dark" sx={{ mb: 0.5, display: 'block' }}>
                        {intl.formatMessage({ id: 'lessonPlan.resourcesHeader' })}
                      </Typography>
                      <Typography variant="body2">{lp.resources || '-'}</Typography>
                      {lp.resourceFiles && lp.resourceFiles.length > 0 && (
                        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 1 }}>
                          {lp.resourceFiles.map((file) => (
                            <Chip
                              key={file.id}
                              icon={<FileOutlined />}
                              label={file.name}
                              size="small"
                              variant="outlined"
                              color="warning"
                              sx={{ mt: 0.5 }}
                            />
                          ))}
                        </Stack>
                      )}
                    </Paper>
                  </Box>
                </Stack>
              </Paper>
            ))}
          </Stack>
        ) : null}
      </Stack>
    </MainCard>
  );

  // ──────────────── GRID VIEW ────────────────
  const renderGridView = () => (
    <MainCard
      title={intl.formatMessage({ id: 'lessonPlan.lessonPlans' })}
      secondary={
        <Button variant="contained" size="small" startIcon={<PlusOutlined />} onClick={handleAddGridRow}>
          {intl.formatMessage({ id: 'lessonPlan.addRecord' })}
        </Button>
      }
    >
      <TableContainer component={Paper} elevation={0}>
        <Table sx={{ minWidth: 1000 }}>
          <TableHead>
            <TableRow sx={{ bgcolor: alpha(theme.palette.primary.main, 0.08) }}>
              <TableCell sx={{ fontWeight: 600, width: '12%' }}>{intl.formatMessage({ id: 'lessonPlan.table.date' })}</TableCell>
              <TableCell sx={{ fontWeight: 600, width: '22%' }}>{intl.formatMessage({ id: 'lessonPlan.table.lessonAndTopic' })}</TableCell>
              <TableCell sx={{ fontWeight: 600, width: '16%' }}>{intl.formatMessage({ id: 'lessonPlan.table.classDetails' })}</TableCell>
              <TableCell sx={{ fontWeight: 600, width: '20%' }}>{intl.formatMessage({ id: 'lessonPlan.table.methods' })}</TableCell>
              <TableCell sx={{ fontWeight: 600, width: '25%' }}>{intl.formatMessage({ id: 'lessonPlan.table.resources' })}</TableCell>
              <TableCell sx={{ fontWeight: 600, width: '5%' }}>{intl.formatMessage({ id: 'lessonPlan.table.actions' })}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {lessonPlans.map((lp) => (
              <TableRow
                key={lp.id}
                hover
                sx={{ '&:hover .delete-btn': { opacity: 1 } }}
              >
                <TableCell sx={{ verticalAlign: 'top' }}>
                  <TextField
                    fullWidth
                    size="small"
                    type="date"
                    value={lp.dateOfIntroduction}
                    onChange={(e) => handleGridFieldChange(lp.id, 'dateOfIntroduction', e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    variant="outlined"
                    sx={{ '& fieldset': { border: 'none' } }}
                  />
                </TableCell>
                <TableCell sx={{ verticalAlign: 'top' }}>
                  <TextField
                    fullWidth
                    size="small"
                    multiline
                    value={lp.lessonTopic}
                    onChange={(e) => handleGridFieldChange(lp.id, 'lessonTopic', e.target.value)}
                    placeholder={intl.formatMessage({ id: 'lessonPlan.placeholder.lessonTopic' })}
                    variant="outlined"
                    sx={{ '& fieldset': { border: 'none' } }}
                  />
                </TableCell>
                <TableCell sx={{ verticalAlign: 'top' }}>
                  <Stack spacing={0.5}>
                    <Typography variant="caption" color="text.secondary">{lp.grade} {lp.subject && `\u2022 ${lp.subject}`}</Typography>
                    <Typography variant="caption" color="text.secondary">{lp.volume} {lp.blockNo && `\u2022 ${lp.blockNo}`}</Typography>
                  </Stack>
                </TableCell>
                <TableCell sx={{ verticalAlign: 'top' }}>
                  <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                    {(lp.selectedMethods || []).map((m) => (
                      <Chip key={m} label={intl.formatMessage({ id: `lessonPlan.method.${m}`, defaultMessage: m })} size="small" variant="outlined" sx={{ mt: 0.5 }} />
                    ))}
                    {lp.otherMethod && <Chip label={lp.otherMethod} size="small" variant="outlined" sx={{ mt: 0.5 }} />}
                  </Stack>
                </TableCell>
                <TableCell sx={{ verticalAlign: 'top' }}>
                  <TextField
                    fullWidth
                    size="small"
                    multiline
                    value={lp.resources}
                    onChange={(e) => handleGridFieldChange(lp.id, 'resources', e.target.value)}
                    placeholder={intl.formatMessage({ id: 'lessonPlan.placeholder.resourcesShort' })}
                    variant="outlined"
                    sx={{ '& fieldset': { border: 'none' } }}
                  />
                </TableCell>
                <TableCell sx={{ verticalAlign: 'top' }}>
                  <IconButton
                    size="small"
                    color="error"
                    className="delete-btn"
                    onClick={() => handleDelete(lp.id)}
                    sx={{ opacity: 0, transition: 'opacity 0.2s' }}
                  >
                    <DeleteOutlined />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </MainCard>
  );

  return viewMode === 'individual' ? renderIndividualView() : renderGridView();
};

LessonPlanMode.propTypes = {
  viewMode: PropTypes.string,
  onDataChange: PropTypes.func,
  childName: PropTypes.string,
  childId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  therapistId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  therapistName: PropTypes.string,
  initialPlans: PropTypes.array
};

export default LessonPlanMode;
